import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import TermsOfUsePopup from './TermsOfUsePopup';
import { formatLegalDate } from '../utils/legalDates';
import { safeSessionStorageGet, safeSessionStorageRemove, safeSessionStorageSet } from '../utils/safeAccess';

const DISMISS_KEY_PREFIX = 'terms_notice_dismissed:';

const TermsNoticeBanner = () => {
  const { currentUser, acceptCurrentTerms } = useAuth();
  const termsStatus = currentUser?.terms_status;
  const dismissKey = termsStatus?.active_version ? `${DISMISS_KEY_PREFIX}${termsStatus.active_version}` : null;
  const [isDismissed, setIsDismissed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!dismissKey) {
      setIsDismissed(false);
      return;
    }

    setIsDismissed(safeSessionStorageGet(dismissKey) === '1');
  }, [dismissKey]);

  if (!termsStatus?.requires_reacceptance || termsStatus.enforcement_mode !== 'notice' || isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    if (dismissKey) {
      safeSessionStorageSet(dismissKey, '1');
    }
    setIsDismissed(true);
  };

  const handleAccept = async () => {
    setLoading(true);
    setSubmitError('');

    try {
      const result = await acceptCurrentTerms();
      if (result.success) {
        if (dismissKey) {
          safeSessionStorageRemove(dismissKey);
        }
        setIsDismissed(false);
        setShowModal(false);
      } else {
        setSubmitError(result.error || 'Failed to accept the latest Terms of Use.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="px-3 pt-3">
        <Alert variant="warning" className="mb-0">
          <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
            <div>
              <div className="fw-bold">Updated Terms of Use require your review</div>
              <div className="small mb-0">
                GrabTime Terms {termsStatus.active_version} took effect on {formatLegalDate(termsStatus.active_effective_date)}.
                Please review and accept them by June 7, 2026 to avoid interruption.
              </div>
              {submitError && (
                <div className="small text-danger mt-2">{submitError}</div>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={handleDismiss}>
                Later
              </Button>
              <Button variant="warning" size="sm" onClick={() => setShowModal(true)}>
                Review Terms
              </Button>
            </div>
          </div>
        </Alert>
      </div>

      <TermsOfUsePopup
        show={showModal}
        onAccept={handleAccept}
        onDecline={() => setShowModal(false)}
        loading={loading}
        acceptLabel="Accept Updated Terms"
      />
    </>
  );
};

export default TermsNoticeBanner;
